import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tab, Tabs } from '@mui/material';
import { a11yProps } from '../utils';
import CustomTabPanel from './CustomTabPanel';
import About from '../../../Components/Restaurant/Tabs/About';
import Menu from '../../../Components/Restaurant/Tabs/Menu';
import Reviews from '../../../Components/Restaurant/Tabs/Reviews';

/**
 * Restaurant tabs component managing About, Menu, and Reviews tabs
 * @param {Object} props - Component props
 * @param {number} props.value - Current active tab value
 * @param {Function} props.onTabChange - Tab change handler
 * @param {Object} props.data - Restaurant data
 * @param {Function} props.handleSelectTime - Time selection handler for About tab
 * @param {boolean} props.fullyBooked - Fully booked status
 * @param {number} props.guest - Number of guests
 * @param {string} props.timeError - Time error message
 * @param {string} props.selectedFloor - Selected floor plan ID
 * @param {Function} props.setSelectedFloor - Floor selection handler
 * @param {boolean} props.isLoading - Loading state for time slots
 * @param {Function} props.handleClose2 - Time popover close handler
 * @param {Array} props.openingHours - Available opening hours
 * @param {string} props.selectedTime - Selected time
 * @param {Function} props.setSelectedTime - Time setter
 * @param {string} props.date - Selected date
 * @param {Function} props.onTimeClick - Time click handler
 * @param {Function} props.onDateClick - Date click handler
 * @param {Function} props.onGuestChange - Guest change handler
 * @param {Function} props.onBook - Book handler
 * @param {boolean} props.isAuthenticated - Authentication status
 * @param {Function} props.onLoginRedirect - Login redirect handler
 */
const RestaurantTabs = ({
  value,
  onTabChange,
  data,
  handleSelectTime,
  fullyBooked,
  guest,
  timeError,
  selectedFloor,
  setSelectedFloor,
  isLoading,
  handleClose2,
  openingHours,
  selectedTime,
  setSelectedTime,
  date,
  onTimeClick,
  onDateClick,
  onGuestChange,
  onBook,
  isAuthenticated,
  onLoginRedirect,
}) => {
  return (
    <Box sx={{ width: '100%', mt: 3 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          variant="fullWidth"
          value={value}
          onChange={onTabChange}
          aria-label="restaurant tabs"
        >
          <Tab
            label="About"
            {...a11yProps(0)}
            sx={{
              fontSize: {
                lg: '14px',
                md: '14px',
                sm: '12px',
                xs: '12px',
              },
            }}
          />
          <Tab
            label="Menu"
            {...a11yProps(1)}
            sx={{
              fontSize: {
                lg: '14px',
                md: '14px',
                sm: '12px',
                xs: '12px',
              },
            }}
          />
          <Tab
            label="Review"
            {...a11yProps(2)}
            sx={{
              fontSize: {
                lg: '14px',
                md: '14px',
                sm: '12px',
                xs: '12px',
              },
            }}
          />
        </Tabs>
      </Box>
      
      <CustomTabPanel value={value} index={0}>
        <About
          handleSelectTime={handleSelectTime}
          fullyBooked={fullyBooked}
          guest={guest}
          timeError={timeError}
          selectedFloor={selectedFloor}
          setSelectedFloor={setSelectedFloor}
          isLoading={isLoading}
          handleClose2={handleClose2}
          data={data}
          openingHours={openingHours}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          date={date}
          onTimeClick={onTimeClick}
          onDateClick={onDateClick}
          onGuestChange={onGuestChange}
          onBook={onBook}
          isAuthenticated={isAuthenticated}
          onLoginRedirect={onLoginRedirect}
        />
      </CustomTabPanel>
      
      <CustomTabPanel value={value} index={1}>
        <Menu data={data} />
      </CustomTabPanel>
      
      <CustomTabPanel value={value} index={2}>
        <Reviews data={data} />
      </CustomTabPanel>
    </Box>
  );
};

RestaurantTabs.propTypes = {
  value: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  data: PropTypes.object,
  handleSelectTime: PropTypes.func.isRequired,
  fullyBooked: PropTypes.bool.isRequired,
  guest: PropTypes.number.isRequired,
  timeError: PropTypes.string.isRequired,
  selectedFloor: PropTypes.string,
  setSelectedFloor: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  handleClose2: PropTypes.func.isRequired,
  openingHours: PropTypes.array,
  selectedTime: PropTypes.string,
  setSelectedTime: PropTypes.func.isRequired,
  date: PropTypes.string.isRequired,
  onTimeClick: PropTypes.func.isRequired,
  onDateClick: PropTypes.func.isRequired,
  onGuestChange: PropTypes.func.isRequired,
  onBook: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  onLoginRedirect: PropTypes.func.isRequired,
};

export default RestaurantTabs;
